import React from 'react';
import get from 'lodash.get';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { EMPTY, from, merge, of, zip } from 'rxjs';
import { catchError, mergeMap, takeWhile, tap, map, throttleTime, take, switchMap, filter, share } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import * as Actions from './orders.grid.actions';
import * as HeaderActions from './orders.grid.header.actions';
import { OrdersGridMoveToBatchModalContainer } from '../../../containers/batches/orders-grid.move-to-batch-modal.container';
import { FeatureFlags } from '../../../feature-flags/feature-flags';
import { rateRefreshSubject } from '../../../fulfillment-planner/context';
import { pusherMessageToRateRefreshSubject } from '../../../fulfillment-planner/helpers/pusher-update-rates-mapper';
import MarkAsShippedModal from '../../../orders/mark-as-shipped/container';
import { buildOrdersGridRequestFiltersFromState } from '../../../orders/services/orders-grid-request-service';
import { getSelectedViewFromPathName } from '../../../pages/orders-page/orders-left-nav/orders-left-nav-context';
import { RATES_UPDATED } from '../../../services/pusher-service/pusher-contract-messages';
import * as BatchesActions from '../../../store/batches/batches.actions';
import * as BatchApi from '../../../store/batches/batches.api';
import * as BatchDetailsActions from '../../../store/batches/details/batch.details.actions';
import * as NavActions from '../../../store/batches/nav/batches.nav.actions';
import Global from '../../../store/global-selectors';
import * as OrderDrawerActions from '../../../store/orders/drawer/orders.drawer.actions';
import * as FilteringActions from '../../../store/orders/filtering/orders.filtering.actions';
import * as OrderDetailsApi from '../../../store/orders/order-details/orders.details.api';
import * as OrdersActions from '../../../store/orders/orders.actions';
import { saveUserSetting } from '../../../store/user/user.actions';
import { bulkRateFulfillmentPlans$ } from '../../bulk/bulk.api';
import { createPusherMessageCache } from '../../pusher/message-cache';
import { messages } from '../messages';

export const changeOrderBy = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.ORDER_GRID_CHANGE_ORDERBY),
    map(() => {
      const stores = Global.selectSellerActiveStores(getState());
      const statusViewId = getSelectedViewFromPathName(history?.location?.pathname, stores)?.id;
      if (statusViewId === 'batches') {
        const { fulfillmentPlanSetId } = Global.selectBatchDetailsBatch(getState());
        return BatchDetailsActions.getBatchDetailsOrdersShipmentsMode(fulfillmentPlanSetId);
      }
      return OrdersActions.requestGetOrders();
    }),
  );
};

export const orderGridAddressValidation = (action$, { getState }) => {
  return action$.pipe(
    ofType(HeaderActions.ORDER_GRID_VALIDATE_ADDRESS),
    mergeMap(() => {
      const state = getState();
      const fulfillmentPlanIdsInDrawer = Global.selectSelectedOrdersIds(state);
      const salesOrders = Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, { fulfillmentPlanIds: fulfillmentPlanIdsInDrawer });
      const shipTos = salesOrders.map((order) => order.shipTos).reduce((accumulate, shipTo) => accumulate.concat(shipTo), []);
      return OrderDetailsApi.validateAddress(shipTos).pipe(
        map(() => OrderDrawerActions.validateAddressSuccess(fulfillmentPlanIdsInDrawer)),
        catchError((err) => of(OrderDrawerActions.validateAddressFailure(err))),
      );
    }),
  );
};

export const openMarkShippedModal = (action$, { getState }) => {
  return action$.pipe(
    ofType(HeaderActions.ORDER_GRID_OPEN_MARK_SHIPPED_MODAL),
    mergeMap(({ page }) => {
      const state = getState();
      const fulfillmentPlanIds = Global.selectSelectedOrdersIds(state);
      ModalService.launchModal({
        component: MarkAsShippedModal,
        props: {
          page,
          fulfillmentPlanIds,
        },
      });
      return EMPTY;
    }),
  );
};

export const setCustomColumnConfigurationOnFilterUpdate = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(FilteringActions.ORDERS_AUTOSWITCH_FILTERVIEW_CUSTOM),
    mergeMap((action) => {
      const state = getState();
      const stores = Global.selectSellerActiveStores(state);
      const statusViewId = getSelectedViewFromPathName(history?.location?.pathname, stores)?.id;
      const currentViewId = statusViewId || 'undefined';
      const previousFilterView = action.filterView;
      const previousfilterViewId = previousFilterView.filterViewId;
      const userViewConfigurations = Global.selectOrdersGridUserGridConfigurations(state);
      const customViewColumnConfiguration =
        get(userViewConfigurations, `${currentViewId}.${previousfilterViewId}`) || get(userViewConfigurations, `${currentViewId}.default`);
      return of(Actions.updateCustomViewGridConfiguration(customViewColumnConfiguration));
    }),
  );
};

export const getRatesFromGridEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(HeaderActions.ORDER_GRID_GET_RATES),
    mergeMap(() => {
      const state = getState();
      const fulfillmentPlanIds = Global.selectSelectedOrdersIds(state);
      const areAllSelected = Global.selectSelectedOrdersAreAllOrdersSelected(state);
      const { enableBulkRefreshRatesOnSuperSelect } = new FeatureFlags({
        state: getState(),
      });

      if (fulfillmentPlanIds.length > 0) {
        return of(HeaderActions.orderGridGetRatesRequest(fulfillmentPlanIds));
      }
      if (areAllSelected && enableBulkRefreshRatesOnSuperSelect) {
        const { pathname } = history.location;
        const filters = buildOrdersGridRequestFiltersFromState(state, undefined, pathname);
        return of(HeaderActions.orderGridGetRatesRequest(fulfillmentPlanIds, filters));
      }

      return of(HeaderActions.orderGridClearGetRates());
    }),
  );
};

export const getRatesForGridRequestEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(HeaderActions.ORDER_GRID_GET_RATES_REQUEST),
    throttleTime(1000),
    mergeMap((action) => {
      const { fulfillmentPlanIds, filters } = action;
      if (fulfillmentPlanIds.length === 1) {
        rateRefreshSubject.next({
          fulfillmentPlanId: fulfillmentPlanIds[0],
          isRefreshingRate: true,
        });
      }
      const bulkRequest = filters || { fulfillmentPlanIds };

      const bulkRate$ = bulkRateFulfillmentPlans$(bulkRequest);

      return bulkRate$.pipe(
        mergeMap((response) => {
          const { rateJobGuids } = response;

          const { findMessage } = createPusherMessageCache([RATES_UPDATED]);
          const code = Global.selectSellerDefaultCurrency(getState());

          // Single subscription that matches any of our job guids
          const allMessages$ = findMessage((pusherMsg) => {
            return rateJobGuids.some((guid) => pusherMsg.messageGuid === guid);
          }).pipe(share()); // Share the subscription among all downstream observers

          // For every rate job guid, filter for its specific messages
          const rateMsgs$ = rateJobGuids.map((guid) => {
            return allMessages$.pipe(
              filter((pusherMsg) => pusherMsg.messageGuid === guid),
              tap((pusherMsg) => {
                const { data } = pusherMsg;
                if (data.Orders?.length) {
                  data.Orders.forEach((order) => {
                    const fulfillmentPlanId = order.OrderID?.toString();
                    if (fulfillmentPlanIds.includes(fulfillmentPlanId)) {
                      rateRefreshSubject.next({
                        fulfillmentPlanId,
                        isRefreshingRate: true,
                        rateObject: pusherMessageToRateRefreshSubject(order, code),
                      });
                    }
                  });
                }
              }),
              takeWhile((pusherMsg) => !pusherMsg.data?.final, true),
              switchMap((pusherMessage) => {
                if (pusherMessage.data?.final) {
                  return of(pusherMessage);
                }
                return EMPTY;
              }),
            );
          });

          // Wait for all rate messages to finish
          const rateResult$ =
            rateJobGuids.length === 0
              ? EMPTY
              : zip(...rateMsgs$).pipe(
                  take(1),
                  tap((arrayOfFinalMessages) => {
                    // All jobs complete - stop the spinner and update rates one final time
                    arrayOfFinalMessages.forEach((finalMessage) => {
                      const { data } = finalMessage;
                      if (data.Orders?.length) {
                        data.Orders.forEach((order) => {
                          const fulfillmentPlanId = order.OrderID?.toString();
                          if (fulfillmentPlanIds.includes(fulfillmentPlanId)) {
                            rateRefreshSubject.next({
                              fulfillmentPlanId,
                              isRefreshingRate: false,
                              rateObject: pusherMessageToRateRefreshSubject(order, code),
                            });
                          }
                        });
                      }
                    });
                  }),
                  mergeMap((arrayOfFinalMessages) =>
                    from(
                      arrayOfFinalMessages.map((finalMessage) =>
                        HeaderActions.orderGridRatesUpdated({
                          ...finalMessage.data,
                          jobId: finalMessage.messageGuid,
                          allRatedFulfillmentPlanIds: fulfillmentPlanIds,
                        }),
                      ),
                    ),
                  ),
                );

          if (rateJobGuids.length) {
            return merge(rateResult$, of(HeaderActions.orderGridGetRatesSuccess(response)));
          }
          return merge(rateResult$, of(HeaderActions.orderGridClearGetRates()));
        }),
        catchError((err) => {
          rateRefreshSubject.next({
            fulfillmentPlanId: fulfillmentPlanIds[0],
            isRefreshingRate: false,
          });
          return of(HeaderActions.orderGridGetRatesFailure(err));
        }),
      );
    }),
  );
};

export const getRatesFromGridFailureEpic = (action$) => {
  return action$.pipe(
    ofType(HeaderActions.ORDER_GRID_GET_RATES_FAILURE),
    mergeMap((action) => {
      const { error } = action;
      const baseNotificationConfig = {
        title: <FormattedMessage {...messages.getRateFailureTitle} />,
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: error?.message || JSON.stringify(error),
        duration: 3000,
      };
      NotificationService.showNotification(baseNotificationConfig);
      return EMPTY;
    }),
  );
};

export const addSalesOrderToBatchFromGridEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(HeaderActions.ADD_SALES_ORDERS_TO_BATCH_FROM_ORDERS_GRID),
    mergeMap(({ fulfillmentPlanSetId, filters }) => {
      const state = getState();

      const salesOrderIds = Global.selectSelectedOrdersIds(state);
      const actualFulfillmentPlanIds = Global.selectEntitiesFulfillmentPlanIdsBySalesOrderIds(state, {
        salesOrderIds,
      });
      const fulfillmentPlanIds = actualFulfillmentPlanIds.length > 0 ? actualFulfillmentPlanIds : salesOrderIds;
      return BatchApi.addFulfillmentPlansToBatch(fulfillmentPlanSetId, fulfillmentPlanIds, filters).pipe(
        mergeMap((response) => {
          const fulfillmentPlansInOtherBatches = get(response, 'data.fulfillmentPlansInOtherBatches');
          if (get(fulfillmentPlansInOtherBatches, 'length', 0) > 0) {
            return of(BatchesActions.salesOrderFulfillmentPlansBlockedFromBatch(fulfillmentPlansInOtherBatches, fulfillmentPlanSetId));
          }
          return of(BatchesActions.addSalesOrdersToBatchSuccess());
        }),
      );
    }),
  );
};

export const openAddSalesOrderToBatchFromGridEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(HeaderActions.OPEN_ADD_SALES_ORDER_TO_BATCH_FROM_ORDERS_GRID),
    mergeMap(({ filters }) => {
      const areAllSelected = Global.selectSelectedOrdersAreAllOrdersSelected(getState());
      const orderGridFilters = areAllSelected
        ? buildOrdersGridRequestFiltersFromState(getState(), filters, history?.location?.pathname)
        : undefined;
      const addToBatchModalId = ModalService.launchModal({
        component: OrdersGridMoveToBatchModalContainer,
        props: {
          filters: orderGridFilters,
        },
      });
      const actions = [BatchDetailsActions.moveToBatchModalOpened(addToBatchModalId)];
      const hasFetchedActiveBatches = Global.selectBatchesNavHasFetchedActiveBatches(getState());
      if (!hasFetchedActiveBatches) {
        actions.push(NavActions.getBatchMenuData());
      }
      return of(...actions);
    }),
  );
};

export const saveDrawerVisbilityUserSetting = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.ORDER_GRID_TOGGLE_SIDEBAR_VISIBILITY),
    mergeMap(() => {
      const isSidebarVisible = Global.selectOrdersGridisSidebarVisible(getState());
      const drawerSetting = {
        settingName: 'orders.grid.drawerVisibility',
        settingValue: isSidebarVisible,
      };
      return of(saveUserSetting(drawerSetting));
    }),
  );
};
